import { SelectionModel } from '@angular/cdk/collections';
import { CurrencyPipe, DatePipe, LowerCasePipe, PercentPipe, SlicePipe, UpperCasePipe } from '@angular/common';
import { AfterContentInit, AfterViewInit, Component, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, PipeTransform, ProviderToken, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '@portals/core';
import { AdvancedFilterComponent } from '../advanced-filter';
import { ContainsConditional, ContainsNotConditional, EqualsConditional, EqualsNotConditional, Filter, GreaterThanConditional, GreaterThanOrEqualsConditional, IsConditional, IsNotConditional, LessThanConditional, LessThanOrEqualsConditional, FilterDataType, getFilterParameters, Report } from '@portals/api';
import { Overlay } from '@angular/cdk/overlay';
import { ActivePipe, AntiActivePipe, CountPipe, DynamicPipe, SliceTextPipe, StatusPipe, TimePipe, VideoLengthPipe, ThumbnailPipe } from '../pipes';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { _isNumberValue } from '@angular/cdk/coercion';
import { getObjectValue } from '@portals/utils';
import { IgxExcelExporterService, IgxExcelExporterOptions } from 'igniteui-angular';
import { ReactionPipe } from '../pipes/reaction.pipe';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { environment } from '@portals/environments';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';

export type Pagination = {
	'pagination[start]'?: number;
	'pagination[limit]'?: number;
	start?: number;
	limit?: number;
};

export const defaultQueryFilter: Pagination = {
	'pagination[start]': 0,
	'pagination[limit]': 25,
};

export type TableHeader = {
	text: string;
	key: any;
	type?: 'avatar-circle' | 'avatar-rectangle' | 'collectible_type' | 'seen' | 'flag' | 'active' | 'transcription';
	pipe?: string;
	pipeArgs?: any[];
	hidden?: boolean;
	sortable?: boolean;
	alignment?: 'center' | 'right'; // default value is 'left'
	columnWidth?: string; // sample value '100px'
};

const insert = (array: Array<any>, index: number, newItem: any) => [
	// part of the array before the specified index
	...array.slice(0, index),
	// inserted item
	newItem,
	// part of the array after the specified index
	...array.slice(index),
];

@Component({
	selector: 'portals-table',
	templateUrl: './table.component.html',
	styleUrls: ['./table.component.scss'],
})
export class TableComponent<T = any> extends BaseComponent implements AfterViewInit, OnChanges {
	_pipes: Record<string, ProviderToken<PipeTransform>> = {
		upperCase: UpperCasePipe,
		lowerCase: LowerCasePipe,
		currency: CurrencyPipe,
		percent: PercentPipe,
		date: DatePipe,
		time: TimePipe,
		count: CountPipe,
		reaction: ReactionPipe,
		textSlice: SliceTextPipe,
		active: ActivePipe,
		antiActive: AntiActivePipe,
		status: StatusPipe,
		videoLength: VideoLengthPipe,
		thumbnail: ThumbnailPipe,
	};
	selectedRowIndex = -1;
	selectedRow: any;
	selectable = true;
	removable = true;
	addOnBlur = true;
	selection = new SelectionModel<any>(true, []);
	app = environment.app;
	filterTexts: Array<string> = [];
	filteredData: Array<any> = [];
	unfilteredData: Array<any> = [];
	mappingData: Array<any> = [];
	highlightedRow!: any;
	filterDisabled = false;

	pageLoading = false;
	loaderConfig = {
		animationType: ngxLoadingAnimationTypes.pulse,
		primaryColour: `var(--portals-theme-primary)`,
		secondaryColour: `var(--portals-theme-accent)`,
	};

	conditionals: any = {
		Is: new IsConditional(),
		'Is not': new IsNotConditional(),
		Contains: new ContainsConditional(),
		'Does not contain': new ContainsNotConditional(),
		Equals: new EqualsConditional(),
		'Less than': new LessThanConditional(),
		'Greater than': new GreaterThanConditional(),
		'Does not equal': new EqualsNotConditional(),
		'Less than or equal to': new LessThanOrEqualsConditional(),
		'Greater than or equal to': new GreaterThanOrEqualsConditional(),
	};

	@Input() columns!: TableHeader[];
	@Input() exportColumns: TableHeader[] | undefined;
	@Input() dataSource!: MatTableDataSource<T>;
	@Input() dataState!: any;
	@Input() filteredDataSource!: MatTableDataSource<T>;
	@Input() unfilteredDataSource!: MatTableDataSource<T>;
	@Input() pageSizeOptions: number[] = [25, 50, 100];
	@Input() searchPlaceholder = 'Search';
	@Input() addItemEnabled = true;
	@Input() renameItemEnabled = false;
	@Input() enableSelection = false;
	@Input() enableExternalLink = false;
	@Input() shareable = false;
	@Input() thumbnail = false;
	@Input() exportable = false;
	@Input() editable = false;
	@Input() filter = false;
	@Input() searchFilter = false;
	@Input() viewAll = false;
	@Input() viewToggle = false;
	@Input() sortable = false;
	@Input() unorderedList = true;
	@Input() tags = false;
	@Input() is_active = false;
	@Input() scheduledStatus = false;
	@Input() generateReport = false;
	@Input() toggleAllText = 'Show Inactive';
	@Input() toggleFilterText = 'Hide Inactive';
	@Input() sortTableText = 'Sort Items';
	@Input() totalRows: number = -1;
	@Input() queryFilter: Pagination = defaultQueryFilter;
	@Input() filters: Filter[] = [];
	@Input() filterDataTypes: FilterDataType[] = [];
	@Input() createNewTourAnchorID = 'newAnchor';
	@Output() rowSelected = new EventEmitter<any>();
	@Output() transcriptionSelected = new EventEmitter<any>();
	@Output() sharedRow = new EventEmitter<T>();
	@Output() externalLink = new EventEmitter<T>();
	@Output() editingRow = new EventEmitter<T>();
	@Output() sortItemsOrder = new EventEmitter<T>();
	@Output() addItemPressed = new EventEmitter<T>();
	@Output() renameItemPressed = new EventEmitter<T>();
	@Output() selectionChange = new EventEmitter<any>();
	@Output() dataChange = new EventEmitter<T>();
	@Output() dataChangePaginate = new EventEmitter<Pagination>();
	@Output() dataChangeSort = new EventEmitter<{
		key: string;
		order: string;
	}>();

	@Output() reportsGeneration = new EventEmitter<Report>();
	@Output() dataChangeSearchFilter = new EventEmitter<string>();
	@Output() dataChangeCustomFilter = new EventEmitter<any>();
	@Output() clearAllFilters = new EventEmitter<any>();
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	tableLoading = false;
	pageSize: number = 25;
	currentPage: number = 1;
	title!: string;
	private readonly searchSubject = new Subject<string | undefined>();

	constructor(public http: HttpClient, public dialog: MatDialog, private overlay: Overlay, private router: Router, private dynamicPipe: DynamicPipe, private excelExportService: IgxExcelExporterService, private titleService: Title) {
		super();
	}
	transcriptionClicked(row: any, event: MouseEvent) {
		this.transcriptionSelected.emit(row);
		event.stopPropagation();
	}

	get displayedColumns() {
		const filterHidden = (item: TableHeader) => !item.hidden;
		const columns = this.enableSelection ? ['select', ...this.columns.filter(filterHidden).map((c) => c.key)] : this.thumbnail ? insert([...this.columns.filter(filterHidden).map((c) => c.key)], 1, 'thumbnail') : this.columns.filter(filterHidden).map((c) => c.key);

		this.tags && columns.push('tagsField');
		this.is_active && columns.push('is_active');
		this.scheduledStatus && columns.push('scheduledStatusField');
		this.enableExternalLink && columns.push('externalLink');
		this.shareable && columns.push('share');
		this.editable && columns.push('edit');

		return columns;
	}

	ngOnInit() {
		this.pageLoading = true;
		this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value: any) => this.dataChangeSearchFilter.emit(value));
	}

	ngAfterViewInit() {
		this.setupSortAndPagination(this.dataSource);
		this.title = this.titleService.getTitle();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['dataSource'] && changes['dataSource']['currentValue'] !== changes['dataSource']['previousValue']) {
			this.pageLoading = true;
			this.setupSortAndPagination(this.dataSource);
		}

		this.pageLoading = false;
	}

	setupSortAndPagination(dataSource: MatTableDataSource<any>) {
		if (!dataSource) {
			return;
		}
		const customSortingDataAccessor = (data: any, sortHeaderId: string) => {
			const value = sortHeaderId.indexOf('.') !== -1 ? getObjectValue(data, sortHeaderId) : sortHeaderId == 'schedule_date' ? new Date(data.schedule_date) : sortHeaderId == 'createdAt' ? new Date(data.createdAt) : sortHeaderId == 'publishedAt' ? new Date(data.publishedAt) : data[sortHeaderId];

			return _isNumberValue(value) ? Number(value) : value;
		};

		const customFilterPredicate = (data: any, filter: string): boolean => {
			const json = JSON.stringify(data).toLowerCase();

			return json.includes(filter.toLowerCase());
		};

		dataSource.paginator = this.paginator;
		dataSource.sort = this.sort;
		dataSource.filterPredicate = customFilterPredicate;
		dataSource.sortingDataAccessor = customSortingDataAccessor;
	}

	applyFilter(event: Event) {
		if (!this.dataSource) {
			return;
		}

		const filterValue = (event.target as HTMLInputElement).value;

		if (this.totalRows != -1) {
			this.searchSubject.next(filterValue.trim());
			return;
		}

		typeof filterValue === typeof 0 ? (this.dataSource.filter = filterValue.trim()) : (this.dataSource.filter = filterValue.trim().toLowerCase());

		this.dataSource.paginator && this.dataSource.paginator.firstPage();
	}

	isAllSelected() {
		const numSelected = this.selection.selected.length;
		const numRows = this.dataSource.data.length;
		return numSelected === numRows;
	}

	masterToggle() {
		if (this.isAllSelected()) {
			this.selection.clear();
			return;
		}

		this.selection.select(...this.dataSource.data);
	}

	selectRow(row?: any) {
		this.selectedRow = row;
		this.selectionChange.emit(this.selection.selected);
	}

	highlight(index: number) {
		if (index >= 0 && index <= this.dataSource.filteredData.length - 1) this.selectedRowIndex = index;
		// console.log(this.selectedRowIndex);
	}

	arrowUpEvent(index: number) {
		this.highlight(index);
		this.highlightedRow = this.dataSource.filteredData[index - 1];
		// console.log(this.highlightedRow);
	}

	arrowDownEvent(index: number) {
		this.highlight(index);
		this.highlightedRow = this.dataSource.filteredData[index + 1];
		// console.log(this.highlightedRow);
	}

	advancedFilter() {
		const dialogRef = this.dialog.open(AdvancedFilterComponent, {
			width: 'auto',
			height: 'auto',
			minWidth: '730px',
			maxHeight: '70vh',
			autoFocus: false,
			restoreFocus: false,
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
			data: {
				filterDataTypes: this.filterDataTypes,
				filters: this.filters,
			},
		});

		// Handle advanced filtering after applying filters from dialog
		this.subscribe(
			dialogRef.afterClosed().pipe(
				tap((result) => {
					if (result) {
						// Clear filter texts and filteredData
						this.filterTexts = [];
						this.filteredData = [];

						//Assign new array of filters
						this.filters = result;
						this.filterDisabled = true;

						this.currentPage = 1;
						this.filterData();
						this.applyChange();
					}
				})
			)
		);
	}

	filterData() {
		if (this.totalRows != -1) {
			const customFilter: any = getFilterParameters(this.filters);
			this.dataChangeCustomFilter.emit({
				filters: this.filters,
				filterParameter: customFilter,
			});
			return;
		}

		// Validate and apply filter logic and texts
		this.filteredData = this.filters.map((filter: Filter) => this.filterItem(filter.condition, this.unfilteredDataSource.data, filter));

		// Get the common elements that subscribe to all conditions
		this.filteredData = this.filterCommonData(this.filteredData);
		this.filteredData.length == 0 ? (this.dataSource = new MatTableDataSource(<any>[])) : ((this.filteredDataSource = new MatTableDataSource(this.filteredData)), this.setupSortAndPagination(this.filteredDataSource));
	}

	filterItem(conditional: string, data: any, input: Filter) {
		const condition = this.conditionals[conditional];
		return condition.filter(data, input);
	}

	filterCommonData(arrays: Array<any>) {
		if (arrays.length == 0) {
			return [];
		}

		const intersection = new Set(arrays[0]);
		for (const array of arrays) {
			const set = new Set(array);
			for (const x of intersection) {
				if (!set.has(x)) intersection.delete(x);
			}
		}
		return Array.from(intersection);
	}

	removeFilter(index: number) {
		let copyFilters = [...this.filters];
		copyFilters.splice(index, 1);
		this.filters = copyFilters;
		this.filterData();

		if (this.filters.length == 0) {
			this.clearFilters();
		}
	}

	clearFilters() {
		// Clear Filter texts and restore original dataSource
		this.filterTexts = [];
		this.filteredData = [];
		this.filters = [];
		this.dataChangeCustomFilter.emit({ filters: [], filterParameter: {} });
		this.reloadComponent(this.router);
	}

	exportData() {
		console.log("testing exportData");
		if (this.dataState) {
			this.subscribe(
				this.dataState.getAllData().pipe(
					tap(({ data }: any) => {
						const dataToExport = data as [];
						// console.log(dataToExport)
						const flattenData = dataToExport.map((item) =>
							this.exportColumns ? this.exportColumns.reduce(
									(acc, header) => ({
										...acc,
										[header.text]: this.dynamicPipe.transform(getObjectValue(item, header.key), this._pipes[header.pipe || ''], header.pipeArgs),
									}),
									{}
								)
								:
							this.columns.reduce(
								(acc, header) => ({
									...acc,
									[header.text]: this.dynamicPipe.transform(getObjectValue(item, header.key), this._pipes[header.pipe || ''], header.pipeArgs),
								}),
								{}
							)
						);

						this.excelExportService.exportData(flattenData, new IgxExcelExporterOptions('Exported Data File'));
					})
				)
			);
		} else {
			const dataToExport = this.dataSource.data;
			const flattenData = dataToExport.map((item) =>
				this.columns.reduce(
					(acc, header) => ({
						...acc,
						[header.text]: this.dynamicPipe.transform(getObjectValue(item, header.key), this._pipes[header.pipe || ''], header.pipeArgs),
					}),
					{}
				)
			);

			this.excelExportService.exportData(flattenData, new IgxExcelExporterOptions('Exported Data File'));
		}
	}

	getStartEntry(): number {
		return this.totalRows > 0 ? this.pageSize * (this.currentPage - 1) + 1 : 0;
	}

	getEndEntry(): number {
		const total = this.pageSize * this.currentPage;
		return total > this.totalRows ? this.totalRows : total;
	}

	pageChanged() {
		this.currentPage = 1;
		this.applyChange();
	}

	isNextDisabled(): boolean {
		const totalPage = Math.ceil(this.totalRows / this.pageSize);
		return totalPage === this.currentPage;
	}

	nextPage() {
		this.tableLoading = true;
		setTimeout(() => {
			if (!this.isNextDisabled()) {
				this.currentPage++;
				this.applyChange();
			}
		}, 1500);
	}

	isPreviousDisabled(): boolean {
		return this.currentPage === 1;
	}

	previousPage() {
		this.tableLoading = true;
		setTimeout(() => {
			if (!this.isPreviousDisabled()) {
				this.currentPage--;
				this.applyChange();
			}
		}, 1500);
	}

	applyChange() {
		this.dataChangePaginate.emit({
			'pagination[start]': this.getStartEntry() - 1,
			'pagination[limit]': this.pageSize,
		});
		this.tableLoading = false;
	}

	sortData(sort: Sort) {
		let { active: key, direction: order } = sort;
		switch (key) {
			case 'channel.image.url':
				key = 'channel.name';
				break;
			case 'image.url':
				key = 'name';
				break;
			case 'scheduledStatusField':
				key = 'state';
				break;
			default:
				break;
		}
		this.dataChangeSort.emit({ key, order });
	}

	genReport() {
		const name = window.prompt('Report Name');
		this.reportsGeneration.emit({
			id: 1,
			name: name ? name : '',
			created_at: new Date(),
			filters: this.filters,
		});
	}

	getFilterChipLabel(filter: Filter): string {
		const {
			value,
			rangeValue,
			field,
			parameter: { nestedKey },
		} = filter || {};
		let label = '';

		if (field !== 'duration') {
			if (!value && rangeValue.length) {
				const isObject = typeof rangeValue[0] === 'object';
				if (!isObject) {
					label = rangeValue.join(', ');
				} else {
					label = rangeValue.map(({ label }: any) => label).join(', ');
				}
			} else {
				label = value;
			}
		} else {
			const hhMmSs = (seconds: number) => new Date(seconds * 1000).toISOString().substring(11, 19);
			if (!value && rangeValue.length) {
				label = `${hhMmSs(+rangeValue[0])}-${hhMmSs(+rangeValue[1])}`;
			} else {
				label = hhMmSs(+value);
			}
		}

		if (nestedKey) {
			label = `${nestedKey}, ${label}`;
		}

		return label;
	}

	protected readonly environment = environment;
}
